// NotesApp source file: header.component.ts

import {
  ComponentFactoryResolver,
  Input,
  Output,
  EventEmitter,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  computed,
  inject,
  SimpleChanges,
} from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable, Subject, Subscription, take } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DatePickerModule } from 'primeng/datepicker';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { ListboxModule } from 'primeng/listbox';
import { Popover, PopoverModule } from 'primeng/popover';
import { InputNumberModule } from 'primeng/inputnumber';

// Notes-App Services
import { Note, Project, DataService, Tag } from '../../../_service/data.service';
import { Space } from '../../../_service/space.service';
import { FilterParams, FilterService, SavedSearch } from '../../../_service/filter.service';
import { AuthService } from '../../../_service/auth.service';
import { LayoutService } from '../../../_service/layout.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { LogService } from '../../../_service/log.service';
import { AppConfig } from '../../user-settings/app.config';
import { getDataTarget } from '../../../_util/data-target';
import { DataTargetService } from '../../../_service/data-target.service';
import { DialogType, GlobalDialogService } from '../../../_service/global-dialog.service';
import { SelectButtonModule } from 'primeng/selectbutton';
import { readListSortState } from '../list/list.component';
import {
  TAG_FILTER_SCOPE,
  TAG_SCOPE,
  TagFilterScope,
  TagSuggestionGroup,
  getTagScope,
  groupTagsByScope,
  normalizeTagFilterScope,
} from '../../../_util/tag-scope.util';
import { focusFieldWhenSettled } from '../../../_util/stable-focus.util';
import { TASK_SEARCH_TYPES } from '../logic/filter.logic';
import { NOTE_STATUSES } from '../../../_util/note-status.util';

type SyncState = 'ok' | 'syncing' | 'offline' | 'error';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    TieredMenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    AppConfig,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    DatePickerModule,
    SplitButtonModule,
    MultiSelectModule,
    ListboxModule,
    PopoverModule,
    InputNumberModule,
    SelectButtonModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() projects: Project[] = [];
  @Input() tags: Tag[] = [];
  /** tasks/frontend-tagging-phase3.md — false while private tag assignments can't be loaded in bulk
   * (server lacks the endpoint): the Private scope is then disabled rather than silently incomplete. */
  @Input() privateTagFilterAvailable = false;
  @Input() spaces: Space[] = [];
  @Input() statusValues: string[] = [...NOTE_STATUSES];
  @Input() types: string[] = [];
  @Input() isOnline: boolean = false;
  @Input() connectivityLabel: string = 'Online mode';
  @Input() isSelectMode: boolean = false;
  @Input() externalFilter: FilterParams | null = null;
  /** Any filter or search is on: the Clear button turns red (grey otherwise). */
  @Input() filterActive = false;
  @Input() clearFilterTooltip: string = 'Clear search and reset all filters\n\nCurrently active filter: none';
  @Output() headerMenuAction = new EventEmitter<string>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() filterChanged = new EventEmitter<any>(); // you can use a proper type here
  @Output() newNote = new EventEmitter<void>();
  /** Mobile-only Sort button (see mobileSortMenuItems). Emits one of ListComponent's own sortBy
   * keys ('Modified' | 'Created' | 'ReferenceDate' | 'Title' | 'Space'); re-selecting the same field toggles
   * its order there, so nothing about direction needs to round-trip back to the header. */
  @Output() sortRequested = new EventEmitter<string>();

  /** The mobile search field now lives in HomeComponent's own template (a floating bottom bar, kept
   * OUTSIDE the Home zoom-scaled layer so it stays pinned to the true viewport edge — see
   * home.component.html). Home hands us the native element (its own `#MobileSearchField` template
   * variable) via this Input so focus/clear behavior below keeps working exactly as before, just
   * pointed at an externally-owned element. */
  @Input() mobileSearchFieldEl?: HTMLInputElement;
  @ViewChild('DesktopSearchField') DesktopSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('desktopFilterRow') desktopFilterRow?: ElementRef<HTMLElement>;
  @ViewChild('menu') menu!: TieredMenu;
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('advancedTagPopover') advancedTagPopover?: Popover;
  @ViewChild('quickTagSearchField') quickTagSearchField?: ElementRef<HTMLInputElement>;
  /** The mobile search-type popup stays here (its overlay is appendTo="body", so where the trigger
   * physically lives doesn't matter); Home's floating bar toggles it via toggleMobileSearchTypeMenu(). */
  @ViewChild('mobileSearchTypeMenu') mobileSearchTypeMenu!: Menu;

  private readonly defaultStatusFilter = '(All)';
  private readonly activeStatusFilter = '(All active)';
  data: Note[] = [];
  filter: FilterParams = {};
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  MobileFilterMenuIsVisible: boolean = false;
  MobileAppMenuIsVisible: boolean = false;
  selectedStatus: string | null = null;
  statusFilterOptions: string[] = [];

  // ── Space filter (tasks/space_ux.md) ─────────────────────────────────────────
  /** Sentinel id for the multiselect's own "All" entry — never a real Space id. */
  readonly SPACE_FILTER_ALL = '__all__';
  /** `shared`: a SHARED Space — shown red with the "group" icon, like the Space column in the List table. */
  spaceFilterOptions: Array<{ id: string; label: string; shared?: boolean }> = [{ id: this.SPACE_FILTER_ALL, label: 'All' }];
  selectedSpaceIds: string[] = [this.SPACE_FILTER_ALL];
  /** Mobile-only: the "🌐 Spaces..." Filter-menu entry opens this dialog (same control as the
   * desktop inline multiselect — see #spaceFilterContent). */
  spaceFilterDialogVisible = false;
  // ─────────────────────────────────────────────────────────────────────────────

  layoutService: LayoutService = inject(LayoutService);
  selectedProject: Project | undefined;
  /** Static app brand shown in the desktop header (mobile shows the shorter "Notes" directly in
   * the template). This is on-page content, not the browser tab title — that's kept separately
   * short (page name / note title / "Edit: ...") via `Title.setTitle()` calls elsewhere. */
  /** The active Home view (List, Tasks, Calendar, Tags…): the header title names it. */
  @Input() selectedView = 'List';

  /** Header title for the active view: "Notes" (List), "Tasks", "Calendar"; other views say what they show. */
  get viewTitle(): string {
    switch (this.selectedView) {
      case 'Tasks':
        return 'Tasks';
      case 'Calendar':
        return 'Calendar';
      case 'Tags':
        return 'Tags';
      default:
        return 'Notes';
    }
  }

  isDarkMode = computed(() => this.layoutService.appState().darkMode);
  dateFilterField: 'modified' | 'created' = 'modified';
  dateFilterMode: 'days' | 'date' = 'days';
  dateSinceDays: number | null = null;
  dateSinceDate: Date | null = null;
  dateFieldOptions = [
    { label: 'Modified Date', value: 'modified' as const },
    { label: 'Creation Date', value: 'created' as const },
  ];
  dateModeOptions = [
    { label: 'Within last N days', value: 'days' as const },
    { label: 'Since specific date', value: 'date' as const },
  ];
  sinceDayQuickOptions = [
    { label: '1 day', value: 1 },
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
  ];
  filterRowMode: 'full' | 'compact-actions' | 'compact-priority' = 'full';
  private filterRowResizeObserver?: ResizeObserver;
  private stopSearchFieldFocusWatch?: () => void;
  private filterLayoutScheduled = false;

  // ── Advanced tag filter ──────────────────────────────────────────────────────
  private readonly TAG_FILTER_SESSION_KEY = 'notesapp.tagFilter.v1';
  /** Whether the advanced tag filter dialog is open. */
  advancedTagDialogVisible = false;
  /** Draft include-tag IDs shown in the advanced panel before Apply is clicked. */
  pendingIncludeTagIds: string[] = [];
  /** Draft exclude-tag IDs shown in the advanced panel before Apply is clicked. */
  pendingExcludeTagIds: string[] = [];
  quickTagSearch = '';
  /** Draft include mode shown in advanced panel before Apply is clicked. */
  pendingIncludeTagMode: 'AND' | 'OR' = 'AND';
  /** Scope being edited in the Advanced Tag Filter (applied with Apply, like the tag lists). */
  pendingTagScope: TagFilterScope = TAG_FILTER_SCOPE.ANY;
  readonly TAG_FILTER_SCOPE = TAG_FILTER_SCOPE;
  readonly TAG_SCOPE = TAG_SCOPE;
  includeTagModeOptions = [
    { label: 'All selected tags (AND)', value: 'AND' as const },
    { label: 'Any selected tag (OR)', value: 'OR' as const },
  ];
  // ─────────────────────────────────────────────────────────────────────────────

  pageMenuItems: MenuItem[] = [];
  filterMenuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  mobileSearchTypeMenuItems: MenuItem[] = [];
  /** Mobile-only compact "Saved Searches" trigger — the same items the hamburger menu's "Saved
   * Searches/Filters" submenu builds, refreshed on open in toggleSavedSearchMenu(). */
  mobileSavedSearchMenuItems: MenuItem[] = [];
  /** Mobile-only Sort button items — rebuilt from the persisted List sort every time the menu opens
   * (openMobileSortMenu), so the current field is checked and shows its direction. */
  mobileSortMenuItems: MenuItem[] = [];
  clearMenuItems: MenuItem[] = [];
  savedSearches: SavedSearch[] = [];
  private userSettingsSub?: Subscription;
  activeSavedSearchId: string | null = null;
  selectedSavedSearchId: string | null = null;
  private isApplyingSavedSearch = false;
  private readonly defaultSearchType = 'Title, Keywords & Tags';
  private readonly defaultSemanticTopK = 10;
  private readonly searchTypeOptions = [
    'Semantic',
    'Full text',
    'Title only',
    'Title & Tags',
    'Tags only',
    'Title & Keywords',
    'Title, Keywords & Tags',
    'Keywords only',
    'ID',
    ...TASK_SEARCH_TYPES,
  ];
  synchLog$: Observable<string[]>;

  // Search-as-you-type debounce: the input itself must feel instant (the ngModel
  // binding on filter.SearchString updates every keystroke), but re-filtering the
  // full note list — or, in semantic mode, firing an API request — on every single
  // keystroke made typing feel laggy and wasteful. Emit searchChanged at most once
  // per pause in typing instead.
  private readonly searchQuery$ = new Subject<string>();
  private searchQuerySub?: Subscription;

  constructor(
    public auth: AuthService,
    private dataService: DataService,
    private router: Router,
    private filterService: FilterService,
    private userSettingsService: UserSettingsService,
    private cdr: ChangeDetectorRef,
    private gDS: GlobalDialogService,
    private logs: LogService,
    public dataTarget: DataTargetService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
    this.searchQuerySub = this.searchQuery$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((query) => this.searchChanged.emit(query));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleDesktopFilterLayoutUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalFilter'] && this.externalFilter) {
      this.filter = this.cloneFilter(this.externalFilter);
      this.selectedStatus = this.filter?.Status ?? null;
      this.selectedSpaceIds = this.filter.SpaceIds?.length ? [...this.filter.SpaceIds] : [this.SPACE_FILTER_ALL];
      this.syncDateFilterStateFromFilter();
      this.buildMobileSearchTypeMenuItems();
      this.cdr.markForCheck();
    }

    if (changes['statusValues']) {
      this.buildStatusFilterOptions();
    }

    if (changes['spaces']) {
      this.buildSpaceFilterOptions();
    }
  }

  // ##############################################################################
  // INITIATION & CLEAN-UP FUNCTIONS
  // ##############################################################################

  async ngOnInit() {
    console.log('HeaderComponent - in ngOnInit()');

    try {
      // 2. UI setup
      this.filter = {
        SearchString: '',
        SearchType: this.defaultSearchType,
        SemanticTopK: this.defaultSemanticTopK,
        TagIds: [],
        TagIncludeMode: 'AND',
        Type: '',
        ProjectName: '',
        Status: this.defaultStatusFilter,
        ModifiedBetween: { start: null, end: null },
        CreatedBetween: { start: null, end: null },
        DateFilterField: 'modified',
        DateFilterMode: 'days',
        DateSinceDays: null,
        DateSinceDate: null,
      };
      this.buildStatusFilterOptions();
      this.userSettingsService.loadSavedTheme();
      //this.userSettingsService.setZoomFactor(151);
      //document.documentElement.style.setProperty('--fontScaleFactor', `151%`);

      this.initializeMenus();
      this.userSettingsSub = this.userSettingsService.userSettings$.subscribe((settings) => {
        this.savedSearches = this.normalizeSavedSearches(settings?.savedSearches);
        if (this.selectedSavedSearchId && !this.savedSearches.some((search) => search.id === this.selectedSavedSearchId)) {
          this.selectedSavedSearchId = null;
        }
        if (this.activeSavedSearchId && !this.savedSearches.some((search) => search.id === this.activeSavedSearchId)) {
          this.activeSavedSearchId = null;
        }
        this.initializeMenus();
      });
      // 6. Filter restoration
      const navigation = this.router.getCurrentNavigation();
      if (navigation?.extras?.state?.['filter']) {
        this.filter = navigation.extras.state['filter'];
      } else {
        this.filter = this.filterService.getFilterParams();
      }
      if (!this.filter.SemanticTopK || this.filter.SemanticTopK < 1) {
        this.filter.SemanticTopK = this.defaultSemanticTopK;
      }
      if (this.filter.TagIncludeMode !== 'OR') {
        this.filter.TagIncludeMode = 'AND';
      }
      if (this.filter?.Status === '(all statuses)') {
        this.filter.Status = undefined;
      }
      if (!this.filter?.Status) {
        this.filter.Status = this.defaultStatusFilter;
      }
    this.buildStatusFilterOptions();
    this.selectedStatus = this.filter?.Status ?? null;
    this.syncDateFilterStateFromFilter();
    this.applyDateFilterToRanges();
    this.buildMobileSearchTypeMenuItems();
    // Restore advanced tag filter from session storage (only include/exclude, no CouchDB persistence).
    this.loadTagFilterFromSession();
    this.filterChanged.emit(this.filter); // ?? Is this necessary
    console.log('ngOnInit complete');
    } catch (err: any) {
      console.error('Unexpected initialization error:', err);
      this.gDS.showGlobalDialog(
        'Critical Error',
        'Initialization failed',
        err?.message || 'Unexpected app crash during init.',
        DialogType.Error,
      );
    }
  }

  ngAfterViewInit(): void {
    // The visible search field receives focus on load — once the layout has settled, and refocused if it
    // still moves (zoom/header layout applied late, login keyboard closing), so on iPad the caret is drawn
    // in the field and never left behind below it (see stable-focus.util.ts).
    this.stopSearchFieldFocusWatch = focusFieldWhenSettled(() => {
      const mobile = this.mobileSearchFieldEl;
      const desktop = this.DesktopSearchField?.nativeElement;
      if (mobile && this.isVisible(mobile)) return mobile;
      if (desktop && this.isVisible(desktop)) return desktop;
      return null;
    });

    setTimeout(() => {
      this.updateDesktopFilterLayout();

      const row = this.desktopFilterRow?.nativeElement;
      if (row && typeof ResizeObserver !== 'undefined') {
        this.filterRowResizeObserver = new ResizeObserver(() => this.scheduleDesktopFilterLayoutUpdate());
        this.filterRowResizeObserver.observe(row);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userSettingsSub) {
      this.userSettingsSub.unsubscribe();
      this.userSettingsSub = undefined;
    }
    if (this.filterRowResizeObserver) {
      this.filterRowResizeObserver.disconnect();
      this.filterRowResizeObserver = undefined;
    }
    this.searchQuerySub?.unsubscribe();
    this.searchQuery$.complete();
    this.stopSearchFieldFocusWatch?.();
  }

  private scheduleDesktopFilterLayoutUpdate(): void {
    if (this.filterLayoutScheduled) {
      return;
    }

    this.filterLayoutScheduled = true;
    requestAnimationFrame(() => {
      this.filterLayoutScheduled = false;
      this.updateDesktopFilterLayout();
    });
  }

  private updateDesktopFilterLayout(): void {
    const row = this.desktopFilterRow?.nativeElement;
    if (!row || !this.isVisible(row)) {
      if (this.filterRowMode !== 'full') {
        this.filterRowMode = 'full';
        this.cdr.markForCheck();
      }
      return;
    }

    const OVERFLOW_TOLERANCE_PX = 2;
    const hasOverflow = () => row.scrollWidth - row.clientWidth > OVERFLOW_TOLERANCE_PX;

    const applyMode = (mode: 'full' | 'compact-actions' | 'compact-priority') => {
      if (this.filterRowMode !== mode) {
        this.filterRowMode = mode;
        this.cdr.detectChanges();
      }
    };

    // Evaluate in strict priority order for consistent behavior.
    applyMode('full');
    if (!hasOverflow()) {
      return;
    }

    applyMode('compact-actions');
    if (!hasOverflow()) {
      return;
    }

    applyMode('compact-priority');
  }

  initializeMenus(): void {
    // Page Menu
    this.pageMenuItems = [
          {
            label: 'View',
            icon: 'pi pi-eye',
            styleClass: '!custom-menu-text',
            items: [
              {
                label: 'Calendar',
                icon: 'pi pi-calendar',
                command: () => {
                  this.onSelectPageMenuItem('Calendar');
                },
              },
              {
                label: 'List',
                icon: 'pi pi-bars',
                command: () => {
                  this.onSelectPageMenuItem('List');
                },
              },
              {
                label: 'Tags',
                icon: 'pi pi-tags',
                command: () => {
                  this.onSelectPageMenuItem('Tags');
                },
              },
              {
                label: 'Tasks',
                icon: 'pi pi-check-square',
                command: () => {
                  this.onSelectPageMenuItem('Tasks');
                },
              },
            ],
          },
          {
            label: 'New Calendar Window',
            icon: 'pi pi-calendar-plus',
            command: () => {
              this.openNewCalendarWindow();
            },
          },
          { separator: true },
          {
            label: 'Refresh page',
            icon: 'pi pi-sync',
            command: () => {
              this.onSelectPageMenuItem('Refresh');
            },
            styleClass: '',
          },
          { separator: true },
          {
            label: 'Presets',
            icon: 'pi pi-bookmark',
            items: this.buildSavedSearchMenuItems(),
          },
          { separator: true },
          {
            label: 'Select mode',
            command: () => {
              this.onSelectPageMenuItem('Select');
            },
            styleClass: '',
          },
          {
            label: 'Download Notes as JSON',
            icon: 'pi pi-download',
            command: () => {
              this.onSelectPageMenuItem('DownloadJson');
            },
            styleClass: '',
          },
          {
            label: 'Import JSON Note(s)...',
            icon: 'pi pi-upload',
            command: () => {
              this.onSelectPageMenuItem('ImportJson');
            },
            styleClass: '',
          },
          {
            label: 'View Import Log',
            icon: 'pi pi-list',
            command: () => {
              this.onSelectPageMenuItem('ViewImportLog');
            },
            styleClass: '',
          },
          {
            label: 'Check for Updates',
            icon: 'pi pi-cloud-download',
            command: () => {
              this.onSelectPageMenuItem('CheckForUpdate');
            },
            styleClass: '',
          },
          {
            label: 'Manage',
            icon: 'pi pi-cog',
            command: () => {
              this.onSelectPageMenuItem('Manage');
            },
            styleClass: '',
          },
          {
            // tasks/delete_trash_client.md — deleted notes, restorable for 30 days.
            label: 'Trash',
            icon: 'pi pi-trash',
            command: () => {
              this.onSelectPageMenuItem('Trash');
            },
          },
          {
            label: 'Synchronisation...',
            icon: 'pi pi-sync',
            command: () => {
              this.onSelectPageMenuItem('Synchronisation');
            },
            styleClass: '',
          },
          {
            label: 'Ask Notes...',
            icon: 'pi pi-sparkles',
            command: () => {
              this.onSelectPageMenuItem('AI');
            },
          },
          {
            label: 'AI Administration',
            icon: 'pi pi-microchip',
            command: () => this.onSelectPageMenuItem('DebugAI'),
            styleClass: '',
          },
          {
            label: 'Reports',
            icon: 'pi pi-chart-bar',
            items: [
              {
                label: 'NotesApp Errors',
                icon: 'pi pi-exclamation-triangle',
                command: () => this.onSelectPageMenuItem('ReportNotesappErrors'),
              },
              {
                label: 'AI Requests Log',
                icon: 'pi pi-sparkles',
                command: () => this.onSelectPageMenuItem('ReportAiRequestsLog'),
              },
              {
                label: 'AI Limits',
                icon: 'pi pi-gauge',
                command: () => this.onSelectPageMenuItem('ReportAiLimits'),
              },
            ],
          },
    ];

    // Filter Menu (mobile). Exact order per request: Favorites, then the three relative-date
    // shortcuts, then Tags, then the two status shortcuts, then Clear Filters (red/bold, always
    // last). Favorites and Tags call the same underlying methods as their (now-removed) duplicate
    // entries in the Saved Searches menu, so there's still one source of truth for each action.
    this.filterMenuItems = [
      {
        items: [
          {
            label: '❤️ Favorites',
            command: () => this.applyFavoritesFilter(),
          },
          { separator: true },
          {
            label: 'Last 30 Days',
            command: () => {
              this.onSelectFilterMenuItem('Last30Days');
            },
          },
          {
            label: 'Last 7 Days',
            command: () => {
              this.onSelectFilterMenuItem('LastWeek');
            },
          },
          {
            label: 'Last 24 Hours',
            command: () => {
              this.onSelectFilterMenuItem('Last24Hours');
            },
          },
          { separator: true },
          {
            label: '🌐 Spaces...',
            command: () => {
              this.spaceFilterDialogVisible = true;
            },
          },
          {
            label: '🏷️ Tags...',
            command: () => this.openAdvancedTagFilterDialog(),
          },
          { separator: true },
          {
            label: 'Action Required',
            command: () => {
              this.onSelectFilterMenuItem('NextActions');
            },
          },
          {
            label: 'All Active',
            command: () => {
              this.onSelectFilterMenuItem('AllActive');
            },
          },
          { separator: true },
          {
            label: 'Clear Filters',
            icon: 'pi pi-filter-slash',
            styleClass: 'mobile-clear-filters-item',
            command: () => {
              this.onSelectFilterMenuItem('ClearFilters');
            },
          },
        ],
      },
    ];

    this.userMenuItems = [
      {
        label: 'App Info',
        icon: 'pi pi-info-circle',
        command: () => this.onSelectPageMenuItem('AppInfo'),
      },
      {
        label: 'User Settings',
        icon: 'pi pi-cog',
        command: () => this.onSelectPageMenuItem('UserSettings'),
      },
      {
        label: 'Toggle Dark Mode',
        icon: 'pi pi-moon',
        command: () => this.onSelectPageMenuItem('ToggleDarkMode'),
      },
      {
        label: `Database: ${getDataTarget()} — switch to ${getDataTarget() === 'PROD' ? 'DEV' : 'PROD'}`,
        icon: 'pi pi-database',
        command: () => this.dataTarget.requestSwitch(),
      },
      {
        // Shows who is signed in, e.g. "Logout dan" (plain "Logout" if the token has no username).
        label: ['Logout', this.auth.getUsername()].filter(Boolean).join(' '),
        icon: 'pi pi-sign-out',
        command: () => this.onSelectPageMenuItem('Logout'),
      },
    ];

    this.clearMenuItems = [
      {
        label: 'Clear Search field',
        icon: 'pi pi-search-minus',
        command: () => this.clearSearchField(),
      },
      {
        label: 'Clear Filters',
        icon: 'pi pi-filter-slash',
        command: () => this.clearFilters(),
      },
      {
        label: 'Clear Tags',
        icon: 'pi pi-tags',
        command: () => this.clearTagFilters(),
      },
      {
        label: 'Clear Date',
        icon: 'pi pi-calendar-times',
        command: () => this.clearDateFilters(),
      },
    ];

    this.buildMobileSearchTypeMenuItems();
    this.buildMobileSortMenuItems();
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  // Called when user types a search query
  onSearch(query: string) {
    this.clearSavedSearchSelection();
    this.searchQuery$.next(query);
  }

  // Called when user selects a filter (e.g. category, project, date range)
  emitFilter() {
    this.filterChanged.emit(this.filter);
  }

  onNewNoteClicked() {
    this.newNote.emit();
  }

  togglePageMenu(event: Event, menu: TieredMenu): void {
    // Rebuild dynamic menu items (including saved-search active checkmark)
    // right before displaying the menu.
    this.initializeMenus();
    menu.toggle(event);
  }

  /** Mobile-only compact "Saved Searches" trigger. Rebuilds the same items as the hamburger menu's
   * "Saved Searches/Filters" submenu (active checkmark included) right before showing. */
  toggleSavedSearchMenu(event: Event, menu: Menu): void {
    this.mobileSavedSearchMenuItems = this.buildSavedSearchMenuItems();
    menu.toggle(event);
  }

  onSelectPageMenuItem(item: string): void {
    const actions: Record<string, () => void> = {
      New: () => this.headerMenuAction.emit('New'),
      NewFromTemplate: () => this.headerMenuAction.emit('NewFromTemplate'),
      List: () => this.headerMenuAction.emit('List'),
      Tags: () => this.headerMenuAction.emit('Tags'),
      Tasks: () => this.headerMenuAction.emit('Tasks'),
      Projects: () => this.headerMenuAction.emit('Projects'),
      Calendar: () => this.headerMenuAction.emit('Calendar'),
      Refresh: () => this.headerMenuAction.emit('Refresh'),
      SaveFilter: () => this.saveCurrentFilter(),
      EditSavedSearches: () => this.router.navigate(['/user-settings'], { queryParams: { tab: 'saved-searches' } }),
      OpenTagFilterDialog: () => this.openAdvancedTagFilterDialog(),
      Select: () => this.headerMenuAction.emit('Select'),
      DownloadJson: () => this.headerMenuAction.emit('DownloadJson'),
      ImportJson: () => this.headerMenuAction.emit('ImportJson'),
      ViewImportLog: () => this.headerMenuAction.emit('ViewImportLog'),
      UserSettings: () => this.headerMenuAction.emit('UserSettings'),
      AppInfo: () => this.headerMenuAction.emit('AppInfo'),
      Spaces: () => this.headerMenuAction.emit('Spaces'),
      ToggleDarkMode: () => this.headerMenuAction.emit('ToggleDarkMode'),
      CheckForUpdate: () => this.headerMenuAction.emit('CheckForUpdate'),
      Manage: () => this.headerMenuAction.emit('Manage'),
      ClearFilters: () => this.clearFilters(),
      Synchronisation: () => this.headerMenuAction.emit('Synchronisation'),
      Trash: () => this.headerMenuAction.emit('Trash'),
      AI: () => this.headerMenuAction.emit('AI'),
      DebugAI: () => this.headerMenuAction.emit('DebugAI'),
      ReportNotesappErrors: () => this.headerMenuAction.emit('ReportNotesappErrors'),
      ReportAiRequestsLog: () => this.headerMenuAction.emit('ReportAiRequestsLog'),
      ReportAiLimits: () => this.headerMenuAction.emit('ReportAiLimits'),
      Logout: () => this.headerMenuAction.emit('Logout'),
    };

    const action = actions[item];
    if (action) {
      action();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  onSavedSearchSelected(savedSearchId: string | null): void {
    if (!savedSearchId) {
      this.clearSavedSearchSelection();
      return;
    }

    const savedSearch = this.savedSearches.find((search) => search.id === savedSearchId);
    if (!savedSearch) {
      this.clearSavedSearchSelection();
      return;
    }

    this.applySavedSearch(savedSearch);
    this.focusSearchField();
  }

  private buildSavedSearchMenuItems(): MenuItem[] {
    const savedSearchItems: MenuItem[] = this.savedSearches.map((search) => ({
      label: search.name,
      icon: this.isSavedSearchActive(search) ? 'pi pi-check' : 'pi pi-filter',
      command: () => this.applySavedSearch(search),
    }));

    if (!savedSearchItems.length) {
      savedSearchItems.push({
        label: 'No presets yet',
        icon: 'pi pi-info-circle',
        disabled: true,
      });
    }

    return [
      ...savedSearchItems,
      { separator: true },
      {
        label: 'Edit Presets',
        icon: 'pi pi-cog',
        command: () => this.onSelectPageMenuItem('EditSavedSearches'),
      },
      {
        label: 'Save as Preset...',
        icon: 'pi pi-save',
        command: () => this.onSelectPageMenuItem('SaveFilter'),
      },
    ];
  }

  private normalizeSavedSearches(value: any): SavedSearch[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const toOrderOrUndefined = (input: unknown): number | undefined => {
      const parsed = Number(input);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const toOrder = (input: unknown): number => {
      const parsed = Number(input);
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };

    const indexed = value
      .filter((item) => !!item && typeof item === 'object')
      .map((item, index) => ({
        index,
        ...item,
        id: String(item.id ?? ''),
        name: String(item.name ?? '').trim(),
        displayOrder: toOrderOrUndefined(item.displayOrder),
      }));

    indexed.sort((left, right) => {
      const leftOrder = toOrder(left.displayOrder);
      const rightOrder = toOrder(right.displayOrder);
      const leftHasOrder = leftOrder !== Number.MAX_SAFE_INTEGER;
      const rightHasOrder = rightOrder !== Number.MAX_SAFE_INTEGER;

      if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (leftHasOrder !== rightHasOrder) {
        return leftHasOrder ? -1 : 1;
      }

      return left.index - right.index;
    });

    return indexed
      .filter((item) => item.id.length > 0 && item.name.length > 0)
      .map((item, index) => ({
        ...item,
        displayOrder: toOrderOrUndefined(item.displayOrder) ?? index + 1,
      }));
  }

  private applySavedSearch(savedSearch: SavedSearch): void {
    this.isApplyingSavedSearch = true;
    try {
      // Explicitly clear first so previously active criteria do not leak into the applied preset.
      this.clearSearchAndFilters();

      this.filter.SearchString = savedSearch.searchString ?? '';
      this.filter.SearchType = savedSearch.searchType || this.defaultSearchType;
      this.filter.TagIds = Array.isArray(savedSearch.tagIds) ? [...savedSearch.tagIds] : [];
      this.filter.TagIncludeMode = savedSearch.tagIncludeMode === 'OR' ? 'OR' : 'AND';
      this.filter.ExcludeTagIds = Array.isArray(savedSearch.excludeTagIds) ? [...savedSearch.excludeTagIds] : [];
      // Searches saved before tag scopes existed have no tagScope: ANY.
      this.filter.TagScope = normalizeTagFilterScope(savedSearch.tagScope);
      this.filter.SpaceIds = Array.isArray(savedSearch.spaceIds) ? [...savedSearch.spaceIds] : [];
      this.filter.Type = savedSearch.type ?? '';
      this.filter.ProjectName = savedSearch.projectName ?? '';
      this.filter.Status = savedSearch.status ?? this.defaultStatusFilter;
      this.filter.ModifiedBetween = this.normalizeDateRange(savedSearch.modifiedBetween);
      this.filter.CreatedBetween = this.normalizeDateRange(savedSearch.createdBetween);
      this.filter.DateFilterField = savedSearch.dateFilterField === 'created' ? 'created' : 'modified';
      this.filter.DateFilterMode = savedSearch.dateFilterMode === 'date' ? 'date' : 'days';
      this.filter.DateSinceDays = Number.isFinite(Number(savedSearch.dateSinceDays))
        ? Number(savedSearch.dateSinceDays)
        : null;
      this.filter.DateSinceDate = savedSearch.dateSinceDate ? new Date(savedSearch.dateSinceDate) : null;
      this.activeSavedSearchId = savedSearch.id;
      this.selectedSavedSearchId = savedSearch.id;

      this.syncDateFilterStateFromFilter();
      this.applyDateFilterToRanges();
      this.selectedStatus = this.filter.Status ?? this.defaultStatusFilter;
      this.selectedSpaceIds = this.filter.SpaceIds?.length ? [...this.filter.SpaceIds] : [this.SPACE_FILTER_ALL];

      this.buildMobileSearchTypeMenuItems();
      this.saveTagFilterToSession();
      this.updateFilters(this.filter);
      this.emitFilter();
    } finally {
      this.isApplyingSavedSearch = false;
    }
  }

  private isSavedSearchActive(savedSearch: SavedSearch): boolean {
    if (this.activeSavedSearchId && this.activeSavedSearchId === savedSearch.id) {
      return true;
    }

    const normalizeString = (value?: string | null): string => (value ?? '').trim();
    const normalizeStatus = (value?: string | null): string => value ?? this.defaultStatusFilter;
    const normalizeMode = (value?: 'AND' | 'OR' | null): 'AND' | 'OR' => (value === 'OR' ? 'OR' : 'AND');
    const normalizeArray = (value?: string[] | null): string[] =>
      Array.isArray(value) ? [...value].filter((v) => typeof v === 'string').sort() : [];
    const normalizeDateField = (value?: 'modified' | 'created' | null): 'modified' | 'created' =>
      value === 'created' ? 'created' : 'modified';
    const normalizeDateMode = (value?: 'days' | 'date' | null): 'days' | 'date' =>
      value === 'date' ? 'date' : 'days';
    const normalizeDateDays = (value?: number | null): number | null => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
    };
    const rangeToTime = (date: Date | null): number | null => (date ? date.getTime() : null);

    const currentModified = this.normalizeDateRange(this.filter.ModifiedBetween);
    const currentCreated = this.normalizeDateRange(this.filter.CreatedBetween);
    const savedModified = this.normalizeDateRange(savedSearch.modifiedBetween);
    const savedCreated = this.normalizeDateRange(savedSearch.createdBetween);

    return (
      normalizeString(this.filter.SearchString) === normalizeString(savedSearch.searchString) &&
      normalizeString(this.filter.SearchType || this.defaultSearchType) ===
        normalizeString(savedSearch.searchType || this.defaultSearchType) &&
      normalizeString(this.filter.Type) === normalizeString(savedSearch.type) &&
      normalizeString(this.filter.ProjectName) === normalizeString(savedSearch.projectName) &&
      normalizeStatus(this.filter.Status) === normalizeStatus(savedSearch.status) &&
      normalizeMode(this.filter.TagIncludeMode) === normalizeMode(savedSearch.tagIncludeMode) &&
      JSON.stringify(normalizeArray(this.filter.TagIds)) === JSON.stringify(normalizeArray(savedSearch.tagIds)) &&
      JSON.stringify(normalizeArray(this.filter.ExcludeTagIds)) ===
        JSON.stringify(normalizeArray(savedSearch.excludeTagIds)) &&
      this.activeTagScope === normalizeTagFilterScope(savedSearch.tagScope) &&
      JSON.stringify(normalizeArray(this.filter.SpaceIds)) === JSON.stringify(normalizeArray(savedSearch.spaceIds)) &&
      normalizeDateField(this.filter.DateFilterField) === normalizeDateField(savedSearch.dateFilterField) &&
      normalizeDateMode(this.filter.DateFilterMode) === normalizeDateMode(savedSearch.dateFilterMode) &&
      normalizeDateDays(this.filter.DateSinceDays) === normalizeDateDays(savedSearch.dateSinceDays) &&
      rangeToTime(this.normalizeToStartOfDay(this.filter.DateSinceDate)) ===
        rangeToTime(this.normalizeToStartOfDay(savedSearch.dateSinceDate ?? null)) &&
      rangeToTime(currentModified.start) === rangeToTime(savedModified.start) &&
      rangeToTime(currentModified.end) === rangeToTime(savedModified.end) &&
      rangeToTime(currentCreated.start) === rangeToTime(savedCreated.start) &&
      rangeToTime(currentCreated.end) === rangeToTime(savedCreated.end)
    );
  }

  private normalizeDateRange(
    value?: { start: Date | null; end: Date | null } | null,
  ): { start: Date | null; end: Date | null } {
    const toDate = (input: Date | string | null | undefined): Date | null => {
      if (!input) {
        return null;
      }
      const date = input instanceof Date ? input : new Date(input);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    return {
      start: toDate((value as any)?.start),
      end: toDate((value as any)?.end),
    };
  }

  onSelectFilterMenuItem(item: string): void {
    const actions: Record<string, () => void> = {
      NextActions: () => (this.filter.Status = 'Action Required'), // also matches legacy "Next Action" notes
      Last30Days: () => {
        this.setFilterDateDays(30);
      },
      LastWeek: () => {
        this.setFilterDateDays(7);
      },
      Last24Hours: () => {
        this.setFilterDateDays(1);
      },
      // Kept for the desktop "KB" quick-filter button (header.component.html), even though it's
      // no longer one of the mobile Filter menu's items.
      KnowledgeBase: () => (this.filter.Type = 'Knowledgebase'),
      AllActive: () => (this.filter.Status = this.activeStatusFilter),
      // Mobile Filter menu → "Clear Filters": the search field is emptied too, like the desktop Clear button.
      ClearFilters: () => this.clearSearchAndFilters(),
    };

    const action = actions[item];
    if (action) {
      action();
      this.clearSavedSearchSelection();
      this.selectedStatus = this.filter.Status ?? null;
      this.updateFilters(this.filter);
      this.emitFilter();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  /** Opens a brand-new browser window/tab on the Calendar view, independent of
   * this one — window.open() gives it its own browsing context, so it can't
   * touch this window's state. Router navigation `state` doesn't cross into a
   * new window, so the desired view is passed as a `?view=` query param that
   * HomeComponent.ngOnInit() also reads. */
  openNewCalendarWindow(): void {
    this.openNewHomeWindow('Calendar');
  }

  /** Same as openNewCalendarWindow(), on the List view. */
  openNewListWindow(): void {
    this.openNewHomeWindow('List');
  }

  /** Same as openNewCalendarWindow(), on the Tasks view. */
  openNewTasksWindow(): void {
    this.openNewHomeWindow('Tasks');
  }

  private openNewHomeWindow(view: 'List' | 'Calendar' | 'Tasks'): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/home'], { queryParams: { view } }));
    window.open(url, '_blank', 'noopener');
  }

  setFilterDateDays(days: number) {
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = Number.isFinite(days) && days > 0 ? Math.floor(days) : null;
    this.dateSinceDate = null;
    this.applyDateFilterToRanges();
  }

  onDateFilterFieldChange(value: 'modified' | 'created') {
    this.clearSavedSearchSelection();
    this.dateFilterField = value === 'created' ? 'created' : 'modified';
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateFilterModeChange(value: 'days' | 'date') {
    this.clearSavedSearchSelection();
    this.dateFilterMode = value === 'date' ? 'date' : 'days';
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateSinceDaysChange(value: number | null) {
    this.clearSavedSearchSelection();
    const parsed = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : null;
    this.dateSinceDays = parsed && parsed > 0 ? parsed : null;
    if (this.dateSinceDays !== null) {
      this.dateSinceDate = null;
    }
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateSinceDateChange(value: Date | null) {
    this.clearSavedSearchSelection();
    const normalized = this.normalizeToStartOfDay(value);
    this.dateSinceDate = normalized;
    if (normalized) {
      this.dateSinceDays = null;
    }
    this.applyDateFilterToRanges();
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  onDateQuickPick(days: number): void {
    this.dateFilterMode = 'days';
    this.onDateSinceDaysChange(days);
  }

  private applyDateFilterToRanges(): void {
    let since: Date | null = null;

    if (this.dateFilterMode === 'days') {
      if (this.dateSinceDays !== null && this.dateSinceDays > 0) {
        const start = new Date();
        start.setDate(start.getDate() - this.dateSinceDays);
        since = this.normalizeToStartOfDay(start);
      }
    } else {
      since = this.normalizeToStartOfDay(this.dateSinceDate);
    }

    this.filter.DateFilterField = this.dateFilterField;
    this.filter.DateFilterMode = this.dateFilterMode;
    this.filter.DateSinceDays = this.dateSinceDays;
    this.filter.DateSinceDate = this.dateSinceDate;

    if (this.dateFilterField === 'created') {
      this.filter.CreatedBetween = { start: since, end: null };
      this.filter.ModifiedBetween = { start: null, end: null };
    } else {
      this.filter.ModifiedBetween = { start: since, end: null };
      this.filter.CreatedBetween = { start: null, end: null };
    }
  }

  private syncDateFilterStateFromFilter(): void {
    const filterField = this.filter.DateFilterField;
    const filterMode = this.filter.DateFilterMode;

    if (filterField === 'created' || filterField === 'modified') {
      this.dateFilterField = filterField;
    } else if (this.filter.CreatedBetween?.start) {
      this.dateFilterField = 'created';
    } else {
      this.dateFilterField = 'modified';
    }

    if (filterMode === 'date' || filterMode === 'days') {
      this.dateFilterMode = filterMode;
    } else {
      this.dateFilterMode = 'date';
    }

    const parsedDays = Number(this.filter.DateSinceDays);
    this.dateSinceDays = Number.isFinite(parsedDays) && parsedDays > 0 ? Math.floor(parsedDays) : null;
    this.dateSinceDate = this.normalizeToStartOfDay(this.filter.DateSinceDate ?? null);

    if (!this.dateSinceDays && !this.dateSinceDate) {
      const range = this.dateFilterField === 'created' ? this.filter.CreatedBetween : this.filter.ModifiedBetween;
      this.dateSinceDate = this.normalizeToStartOfDay(range?.start ?? null);
      if (this.dateSinceDate) {
        this.dateFilterMode = 'date';
      }
    }
  }

  private normalizeToStartOfDay(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

  get dateFilterButtonLabel(): string {
    if (!this.isDateFilterActive) {
      return 'Date';
    }

    const prefix = this.dateFilterField === 'created' ? 'Created' : 'Modified';

    if (this.dateFilterMode === 'days' && this.dateSinceDays && this.dateSinceDays > 0) {
      const suffix = this.dateSinceDays === 1 ? 'day' : 'days';
      return `${prefix} within ${this.dateSinceDays} ${suffix}`;
    }

    const date = this.normalizeToStartOfDay(this.dateSinceDate);
    if (!date) {
      return prefix;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${prefix} since ${day}.${month}`;
  }

  get dateFilterTooltipLabel(): string {
    if (!this.isDateFilterActive) {
      return 'Filter by date';
    }
    return `Filter by date: ${this.dateFilterButtonLabel}`;
  }

  private buildMobileSearchTypeMenuItems() {
    this.mobileSearchTypeMenuItems = this.searchTypeOptions.map((type) => ({
      label: type,
      icon: this.filter.SearchType === type ? 'pi pi-check' : '',
      command: () => this.setSearchType(type),
    }));
  }

  /** Mobile-only Sort button (see sortRequested). `field` is one of the sortBy keys
   * ListComponent.sortData()/getVal() understand — re-picking the same one there toggles order.
   * Like the iOS Files sort menu: the active field is checked, with its direction in small grey
   * text underneath and an up/down chevron; tapping it again reverses the direction. */
  private buildMobileSortMenuItems(): void {
    const newestOldest = { desc: 'Newest to oldest', asc: 'Oldest to newest' };
    const latestEarliest = { desc: 'Latest to earliest', asc: 'Earliest to latest' };
    const alphabetical = { asc: 'A to Z', desc: 'Z to A' };
    const fields: Array<{ label: string; field: string; directions: { asc: string; desc: string } }> = [
      { label: 'Modified', field: 'Modified', directions: newestOldest },
      { label: 'Created', field: 'Created', directions: newestOldest },
      { label: 'Reference Date', field: 'ReferenceDate', directions: latestEarliest },
      { label: 'Title', field: 'Title', directions: alphabetical },
      { label: 'Priority', field: 'Priority', directions: { desc: 'Highest to lowest', asc: 'Lowest to highest' } },
      // tasks/space_ux.md — new sort option, kept last per the task's own "at the bottom" wording.
      { label: 'Space', field: 'Space', directions: alphabetical },
    ];
    const current = readListSortState();
    this.mobileSortMenuItems = fields.map(({ label, field, directions }) => {
      const selected = current.sortBy === field;
      return {
        label,
        // Read by the menu's item template in header.component.html.
        state: selected ? { selected: true, direction: directions[current.sortOrder], order: current.sortOrder } : undefined,
        ariaLabel: selected ? `${label}, selected, ${directions[current.sortOrder]}` : label,
        command: () => this.sortRequested.emit(field),
      };
    });
  }

  openMobileSortMenu(menu: Menu, event: Event): void {
    if (!menu.visible) this.buildMobileSortMenuItems();
    menu.toggle(event);
  }

  private buildStatusFilterOptions() {
    const uniqueStatuses = new Set<string>([this.defaultStatusFilter, this.activeStatusFilter, ...this.statusValues]);
    this.statusFilterOptions = Array.from(uniqueStatuses);
  }

  /** tasks/space_ux.md — "All", then every Space the user has at least View access to
   * (getSpaces() already only returns Spaces the user belongs to, with any role). */
  private buildSpaceFilterOptions(): void {
    this.spaceFilterOptions = [
      { id: this.SPACE_FILTER_ALL, label: 'All' },
      ...[...(this.spaces || [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((space) => ({ id: space.id, label: space.name, shared: space.type === 'SHARED' })),
    ];
  }

  setSearchType(type: string) {
    // FILL-IN CODE HERE
    console.log('setSearchType: ', type);
    this.clearSavedSearchSelection();
    this.filter.SearchType = type;
    this.emitFilter();
    this.buildMobileSearchTypeMenuItems();
  }

  setSemanticTopK(value: number | string): void {
    this.clearSavedSearchSelection();
    const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
    this.filter.SemanticTopK = Number.isFinite(parsed) && parsed > 0 ? parsed : this.defaultSemanticTopK;
    this.emitFilter();
  }

  clearSearchField() {
    const mobile = this.mobileSearchFieldEl;
    const desktop = this.DesktopSearchField.nativeElement;

    this.clearSavedSearchSelection();
    this.filter.SearchString = '';
    this.filter.TagIds = [];
    this.resetTagScopeIfNoTags();
    this.emitFilter();
    // this.SearchField.nativeElement.focus();

    if (mobile && this.isVisible(mobile)) {
      mobile.focus();
    } else if (this.isVisible(desktop)) {
      desktop.focus();
    }
  }

  /** Home's floating mobile search-type button calls this — the popup itself stays defined here
   * (appendTo="body" escapes the Home zoom layer regardless of where its trigger lives). */
  toggleMobileSearchTypeMenu(event: Event): void {
    this.mobileSearchTypeMenu.toggle(event);
  }

  clearFilters(): void {
    console.log('clearFilters');
    this.clearSavedSearchSelection();
    this.filter.Type = '';
    this.filter.ProjectName = '';
    this.filter.Status = this.defaultStatusFilter;
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = [];
    this.filter.TagScope = TAG_FILTER_SCOPE.ANY;
    this.filter.FavoritesOnly = false;
    this.filter.SpaceIds = [];
    this.selectedSpaceIds = [this.SPACE_FILTER_ALL];
    this.selectedStatus = this.defaultStatusFilter;
    this.filter.DateFilterField = 'modified';
    this.filter.DateFilterMode = 'days';
    this.filter.DateSinceDays = null;
    this.filter.DateSinceDate = null;
    this.filter.ModifiedBetween = { start: null, end: null };
    this.filter.CreatedBetween = { start: null, end: null };
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = null;
    this.dateSinceDate = null;
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  clearTagFilters(): void {
    this.clearSavedSearchSelection();
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = [];
    this.filter.TagScope = TAG_FILTER_SCOPE.ANY;
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  /** Saved-Search-menu "Favorites" shortcut. The favorited note-ID set itself lives in
   * user-settings (favoriteNoteIds) and is applied by HomeComponent/FilterLogic; this just flips
   * the FavoritesOnly flag on the active filter, same as picking a saved search or a quick preset. */
  applyFavoritesFilter(): void {
    this.clearSavedSearchSelection();
    this.filter.FavoritesOnly = true;
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  // ───── Advanced tag filter ────────────────────────────────────────────────────────

  openAdvancedTagFilterDialog(): void {
    this.pendingIncludeTagIds = [...(this.filter.TagIds ?? [])];
    this.pendingExcludeTagIds = [...(this.filter.ExcludeTagIds ?? [])];
    this.pendingIncludeTagMode = this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
    this.pendingTagScope = normalizeTagFilterScope(this.filter.TagScope);
    this.advancedTagDialogVisible = true;
  }

  openAdvancedFromQuickTag(popover: Popover): void {
    popover.hide();
    this.openAdvancedTagFilterDialog();
  }

  get quickTagOptions(): Tag[] {
    const query = this.quickTagSearch.trim().toLocaleLowerCase();
    const matchingTags = !query
      ? this.sortedTagOptions
      : this.sortedTagOptions.filter((tag) => tag.name.toLocaleLowerCase().includes(query));

    return [...matchingTags].sort((left, right) => {
      const leftSelected = this.isQuickTagSelected(left._id);
      const rightSelected = this.isQuickTagSelected(right._id);
      if (leftSelected !== rightSelected) {
        return leftSelected ? -1 : 1;
      }
      return 0;
    });
  }

  get selectedQuickTagOptions(): Tag[] {
    const selectedTagIds = new Set(this.filter.TagIds ?? []);
    return this.sortedTagOptions.filter((tag) => selectedTagIds.has(tag._id));
  }

  onQuickTagPopoverShow(): void {
    this.focusQuickTagSearchField();
  }

  toggleQuickTagPopover(event: Event, popover: Popover): void {
    popover.toggle(event);
    this.focusQuickTagSearchField();
  }

  isQuickTagSelected(tagId: string): boolean {
    return (this.filter.TagIds ?? []).includes(tagId);
  }

  toggleQuickTag(tagId: string): void {
    this.clearSavedSearchSelection();
    const current = new Set(this.filter.TagIds ?? []);
    if (current.has(tagId)) {
      current.delete(tagId);
    } else {
      current.add(tagId);
    }

    this.filter.TagIds = Array.from(current);
    this.filter.TagIncludeMode = 'AND';
    this.filter.ExcludeTagIds = (this.filter.ExcludeTagIds ?? []).filter((id) => id !== tagId);
    this.resetTagScopeIfNoTags();
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  clearQuickTagFilter(): void {
    this.clearTagFilters();
  }

  // ───── Tag scope (tasks/frontend-tagging-phase3.md) ───────────────────────────────

  get tagScopeOptions(): Array<{ label: string; value: TagFilterScope; icon?: string; disabled?: boolean }> {
    return [
      { label: 'Any', value: TAG_FILTER_SCOPE.ANY },
      { label: 'Space', value: TAG_FILTER_SCOPE.SPACE },
      { label: 'Private', value: TAG_FILTER_SCOPE.PRIVATE, icon: 'pi pi-lock', disabled: !this.privateTagFilterAvailable },
    ];
  }

  get activeTagScope(): TagFilterScope {
    return normalizeTagFilterScope(this.filter.TagScope);
  }

  /** Quick-filter scope change: applies immediately, like the quick tag checkboxes. */
  setQuickTagScope(scope: TagFilterScope): void {
    this.clearSavedSearchSelection();
    this.filter.TagScope = normalizeTagFilterScope(scope);
    this.resetTagScopeIfNoTags();
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  /** Without any tag criterion the scope means nothing, so it goes back to ANY rather than lingering
   * as a hidden setting that would silently affect the next tag the user picks. */
  private resetTagScopeIfNoTags(): void {
    if (!(this.filter.TagIds?.length ?? 0) && !(this.filter.ExcludeTagIds?.length ?? 0)) {
      this.filter.TagScope = TAG_FILTER_SCOPE.ANY;
    }
  }

  /** Space/Private context for a tag in the pickers, so identical names stay distinguishable. */
  tagContextLabel(tag: Tag): string {
    if (getTagScope(tag) === TAG_SCOPE.PRIVATE) return 'Private';
    const space = this.spaces.find((candidate) => candidate.id === tag.spaceId);
    return space ? (space.type === 'PRIVATE' ? 'My Space' : space.name) : 'Space';
  }

  isPrivateTagOption(tag: Tag): boolean {
    return getTagScope(tag) === TAG_SCOPE.PRIVATE;
  }

  /** Grouped options (Space tags / Private tags) for the Advanced Tag Filter multiselects. */
  get groupedTagOptions(): TagSuggestionGroup[] {
    const sorted = this.sortedTagOptions;
    return groupTagsByScope(
      sorted.filter((tag) => !this.isPrivateTagOption(tag)),
      sorted.filter((tag) => this.isPrivateTagOption(tag)),
    );
  }

  /** The quick popover's list, split the same way. */
  get quickTagOptionGroups(): TagSuggestionGroup[] {
    const options = this.quickTagOptions;
    return groupTagsByScope(
      options.filter((tag) => !this.isPrivateTagOption(tag)),
      options.filter((tag) => this.isPrivateTagOption(tag)),
    );
  }

  private focusQuickTagSearchField(): void {
    const focus = () => {
      const input = this.quickTagSearchField?.nativeElement
        ?? Array.from(document.querySelectorAll<HTMLInputElement>('.header-tags-filter-popover__search'))
          .find((element) => this.isVisible(element));

      if (!input) {
        return false;
      }

      input.focus();
      input.select();
      return document.activeElement === input;
    };

    const retryDelays = [0, 16, 48, 120];
    retryDelays.forEach((delay) => {
      setTimeout(() => {
        focus();
      }, delay);
    });
  }

  closeAdvancedTagFilter(): void {
    this.advancedTagPopover?.hide();
    this.advancedTagDialogVisible = false;
  }

  /** Apply the pending draft state and save to session. */
  applyAdvancedTagFilter(): void {
    this.clearSavedSearchSelection();
    this.filter.TagIds = [...this.pendingIncludeTagIds];
    this.filter.TagIncludeMode = this.pendingIncludeTagMode;
    this.filter.ExcludeTagIds = [...this.pendingExcludeTagIds];
    this.filter.TagScope = this.pendingTagScope;
    this.resetTagScopeIfNoTags();
    this.closeAdvancedTagFilter();
    this.updateFilters(this.filter);
    this.saveTagFilterToSession();
    this.emitFilter();
  }

  /** Discard pending changes without applying. */
  cancelAdvancedTagFilter(): void {
    this.closeAdvancedTagFilter();
  }

  /** Clear all tag filters (both include and exclude) and close the panel. */
  clearAdvancedTagFilter(): void {
    this.pendingIncludeTagIds = [];
    this.pendingIncludeTagMode = 'AND';
    this.pendingExcludeTagIds = [];
    this.pendingTagScope = TAG_FILTER_SCOPE.ANY;
    this.applyAdvancedTagFilter();
  }

  /** Called when the pending include list changes inside the advanced panel. Enforces mutual exclusion. */
  onAdvancedIncludeChange(ids: string[]): void {
    const included = new Set(ids);
    // Remove from exclude any tag that is now in include.
    this.pendingExcludeTagIds = this.pendingExcludeTagIds.filter((id) => !included.has(id));
  }

  /** Called when the pending exclude list changes inside the advanced panel. Enforces mutual exclusion. */
  onAdvancedExcludeChange(ids: string[]): void {
    const excluded = new Set(ids);
    // Remove from include any tag that is now in exclude.
    this.pendingIncludeTagIds = this.pendingIncludeTagIds.filter((id) => !excluded.has(id));
  }

  /** True when there are active exclude-tag filters (signals the user there is more than a basic include filter). */
  get isAdvancedTagFilterActive(): boolean {
    return (this.filter.ExcludeTagIds?.length ?? 0) > 0 || this.filter.TagIncludeMode === 'OR';
  }

  get isTagFilterActive(): boolean {
    return (this.filter.TagIds?.length ?? 0) > 0 || this.isAdvancedTagFilterActive;
  }

  /** Names of the included filter tags, in the order they were picked (ids with no known tag are skipped). */
  private get includedTagFilterNames(): string[] {
    const ids = this.filter.TagIds ?? [];
    if (!ids.length) return [];
    const nameById = new Map((this.tags ?? []).map((tag) => [tag._id, tag.name] as const));
    return ids.map((id) => nameById.get(id)).filter((name): name is string => !!name);
  }

  /** Desktop Tags filter button: the selected tag names (the button truncates with an ellipsis; the
   * tooltip has the full list). Falls back to a count if the tags aren't loaded yet. */
  get tagFilterButtonLabel(): string {
    const includeCount = this.filter.TagIds?.length ?? 0;
    if (includeCount === 0) {
      return this.isAdvancedTagFilterActive ? 'Tags +' : 'Tags';
    }
    const names = this.includedTagFilterNames;
    return names.length ? names.join(', ') : `Tags (${includeCount})`;
  }

  get tagFilterTooltipLabel(): string {
    const includeCount = this.filter.TagIds?.length ?? 0;
    const excludeCount = this.filter.ExcludeTagIds?.length ?? 0;
    const mode = this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
    if (!includeCount && !excludeCount) {
      return 'Filter by tags';
    }
    const scope = this.activeTagScope;
    const names = this.includedTagFilterNames;
    const parts = [
      names.length ? `Tags: ${names.join(', ')}` : `Tags: ${includeCount} selected`,
      includeCount > 1 ? `match ${mode === 'OR' ? 'any' : 'all'}` : '',
      excludeCount ? `excluding ${excludeCount}` : '',
      scope === TAG_FILTER_SCOPE.ANY ? '' : `scope: ${scope === TAG_FILTER_SCOPE.PRIVATE ? 'Private' : 'Space'}`,
    ];
    return parts.filter(Boolean).join(' · ');
  }

  /** Compact label shown next to the Advanced button when a filter is active. */
  get tagFilterSummaryLabel(): string {
    const inc = this.filter.TagIds?.length ?? 0;
    const exc = this.filter.ExcludeTagIds?.length ?? 0;
    const parts: string[] = [];
    if (inc > 0) parts.push(`${inc} incl.`);
    if (this.filter.TagIncludeMode === 'OR') parts.push('incl:any');
    if (exc > 0) parts.push(`${exc} excl.`);
    return parts.join(', ');
  }

  /** Persist include/exclude tag IDs to sessionStorage so the filter survives navigation within the session. */
  saveTagFilterToSession(): void {
    try {
      const payload = {
        includeTagIds: this.filter.TagIds ?? [],
        includeTagMode: this.filter.TagIncludeMode === 'OR' ? 'OR' : 'AND',
        excludeTagIds: this.filter.ExcludeTagIds ?? [],
        tagScope: this.activeTagScope,
      };
      sessionStorage.setItem(this.TAG_FILTER_SESSION_KEY, JSON.stringify(payload));
    } catch {
      // sessionStorage may be unavailable in private browsing; silently ignore.
    }
  }

  /** Restore include/exclude tag IDs from sessionStorage on component init. */
  loadTagFilterFromSession(): void {
    try {
      const raw = sessionStorage.getItem(this.TAG_FILTER_SESSION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        includeTagIds?: string[];
        includeTagMode?: 'AND' | 'OR';
        excludeTagIds?: string[];
        tagScope?: string;
      };
      if (Array.isArray(parsed.includeTagIds) && parsed.includeTagIds.length > 0) {
        this.filter.TagIds = parsed.includeTagIds;
      }
      if (parsed.includeTagMode === 'OR') {
        this.filter.TagIncludeMode = 'OR';
      } else {
        this.filter.TagIncludeMode = 'AND';
      }
      if (Array.isArray(parsed.excludeTagIds) && parsed.excludeTagIds.length > 0) {
        this.filter.ExcludeTagIds = parsed.excludeTagIds;
      }
      this.filter.TagScope = normalizeTagFilterScope(parsed.tagScope);
      this.resetTagScopeIfNoTags();
    } catch {
      // Malformed session data; ignore.
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────

  clearDateFilters(): void {
    this.clearSavedSearchSelection();
    this.dateFilterField = 'modified';
    this.dateFilterMode = 'days';
    this.dateSinceDays = null;
    this.dateSinceDate = null;
    this.filter.DateFilterField = 'modified';
    this.filter.DateFilterMode = 'days';
    this.filter.DateSinceDays = null;
    this.filter.DateSinceDate = null;
    this.filter.ModifiedBetween = { start: null, end: null };
    this.filter.CreatedBetween = { start: null, end: null };
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  clearSearchAndFilters(): void {
    this.clearSavedSearchSelection();
    this.filter.SearchString = '';
    this.filter.SearchType = this.defaultSearchType;
    this.filter.SemanticTopK = this.defaultSemanticTopK;
    this.filter.TagIds = [];
    this.filter.TagIncludeMode = 'AND';
    this.buildMobileSearchTypeMenuItems();
    this.clearFilters();
  }

  // Call this method when filters are changed
  updateFilters(newFilters: FilterParams) {
    this.filterService.setFilterParams(newFilters);
  }

  /** Save the current filter/search settings with a user-provided name. */
  saveCurrentFilter(): void {
    const filterName = prompt('Enter a name for this preset:');
    if (!filterName || !filterName.trim()) {
      return; // User cancelled or entered empty string
    }

    // Capture current filter state
    const savedSearch: SavedSearch = {
      id: Date.now().toString(), // Simple unique ID
      name: filterName.trim(),
      searchString: this.filter.SearchString,
      searchType: this.filter.SearchType,
      tagIds: this.filter.TagIds,
      tagIncludeMode: this.filter.TagIncludeMode,
      excludeTagIds: this.filter.ExcludeTagIds,
      tagScope: this.activeTagScope,
      spaceIds: this.filter.SpaceIds,
      type: this.filter.Type,
      projectName: this.filter.ProjectName,
      status: this.filter.Status,
      modifiedBetween: this.filter.ModifiedBetween,
      createdBetween: this.filter.CreatedBetween,
      dateFilterField: this.filter.DateFilterField,
      dateFilterMode: this.filter.DateFilterMode,
      dateSinceDays: this.filter.DateSinceDays,
      dateSinceDate: this.filter.DateSinceDate,
      createdAt: new Date(),
    };

    // Get current saved searches from user settings
    this.userSettingsService.userSettings$.pipe(
      filter((settings) => !!settings),
      take(1),
    ).subscribe((settings) => {
      if (!settings) return;

      const currentSavedSearches = Array.isArray(settings.savedSearches) ? settings.savedSearches : [];
      const nextDisplayOrder = currentSavedSearches.length + 1;
      savedSearch.displayOrder = nextDisplayOrder;
      const updatedSavedSearches = [...currentSavedSearches, savedSearch];

      // Save to user settings (CouchDB + IndexedDB)
      this.userSettingsService.updateSavedSearches(updatedSavedSearches).subscribe(
        (response) => {
          if (response.status === 'success') {
            this.gDS.showGlobalDialog('Success', `Preset "${filterName}" saved`, '', DialogType.Info);
          } else {
            this.gDS.showGlobalDialog('Error', 'Failed to save preset', response.error?.message || '', DialogType.Error);
          }
        },
        (error) => {
          console.error('Error saving search/filter:', error);
          this.gDS.showGlobalDialog('Error', 'Failed to save preset', error?.message || '', DialogType.Error);
        },
      );
    });
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    // offsetParent is always null for position:fixed elements (e.g. the floating mobile search bar in
    // home.component.html) even when they're genuinely on screen, so fall back to getClientRects():
    // empty only when the element (or an ancestor) is actually display:none / not rendered.
    const hasBox = element.offsetParent !== null || element.getClientRects().length > 0;
    return hasBox && style.visibility !== 'hidden' && style.display !== 'none';
  }

  onStatusChanged(value: string | null) {
    this.clearSavedSearchSelection();
    this.selectedStatus = value ?? null;
    this.filter.Status = value && value.trim().length ? value : undefined;
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  /** tasks/space_ux.md — "All" and specific Spaces are mutually exclusive in this multiselect:
   * picking a specific Space drops "All", picking "All" drops everything else, and an empty
   * selection (the last specific pick removed) reverts to "All" rather than showing nothing
   * selected at all. */
  onSpaceFilterChanged(newValue: string[]): void {
    this.clearSavedSearchSelection();
    const hadAll = this.selectedSpaceIds.includes(this.SPACE_FILTER_ALL);
    const hasAllNow = newValue.includes(this.SPACE_FILTER_ALL);

    let resolved: string[];
    if (hasAllNow && !hadAll) {
      resolved = [this.SPACE_FILTER_ALL];
    } else if (newValue.length > 1 && hasAllNow) {
      resolved = newValue.filter((id) => id !== this.SPACE_FILTER_ALL);
    } else if (newValue.length === 0) {
      resolved = [this.SPACE_FILTER_ALL];
    } else {
      resolved = newValue;
    }

    this.selectedSpaceIds = resolved;
    this.filter.SpaceIds = resolved.includes(this.SPACE_FILTER_ALL) ? [] : [...resolved];
    this.updateFilters(this.filter);
    this.emitFilter();
  }

  focusSavedSearchFilterInput(): void {
    setTimeout(() => {
      const input = Array.from(
        document.querySelectorAll<HTMLInputElement>('.header-saved-search-panel .p-select-filter'),
      ).find((element) => this.isVisible(element));
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  private focusSearchField(): void {
    setTimeout(() => {
      const desktop = this.DesktopSearchField?.nativeElement;
      const mobile = this.mobileSearchFieldEl;
      const target = desktop && this.isVisible(desktop) ? desktop : mobile && this.isVisible(mobile) ? mobile : null;
      target?.focus();
      target?.select();
    }, 0);
  }

  private clearSavedSearchSelection(): void {
    this.selectedSavedSearchId = null;
    if (!this.isApplyingSavedSearch) {
      this.activeSavedSearchId = null;
    }
  }

  private cloneFilter(source: FilterParams): FilterParams {
    return {
      ...source,
      TagIds: Array.isArray(source.TagIds) ? [...source.TagIds] : [],
      ExcludeTagIds: Array.isArray(source.ExcludeTagIds) ? [...source.ExcludeTagIds] : [],
      SpaceIds: Array.isArray(source.SpaceIds) ? [...source.SpaceIds] : [],
      ModifiedBetween: {
        start: source.ModifiedBetween?.start ? new Date(source.ModifiedBetween.start) : null,
        end: source.ModifiedBetween?.end ? new Date(source.ModifiedBetween.end) : null,
      },
      CreatedBetween: {
        start: source.CreatedBetween?.start ? new Date(source.CreatedBetween.start) : null,
        end: source.CreatedBetween?.end ? new Date(source.CreatedBetween.end) : null,
      },
      DateSinceDate: source.DateSinceDate ? new Date(source.DateSinceDate) : null,
    };
  }

  get isSearchStringActive(): boolean {
    return !!this.filter.SearchString?.trim();
  }

  get isSearchTypeActive(): boolean {
    const searchType = this.filter.SearchType ?? this.defaultSearchType;
    return searchType !== this.defaultSearchType;
  }

  get isSemanticMode(): boolean {
    return (this.filter.SearchType ?? '').trim() === 'Semantic';
  }

  get isTypeFilterActive(): boolean {
    return !!this.filter.Type?.trim();
  }

  get isProjectFilterActive(): boolean {
    return !!this.filter.ProjectName?.trim();
  }

  get isStatusFilterActive(): boolean {
    const status = this.filter.Status ?? this.defaultStatusFilter;
    return !!status && status !== this.defaultStatusFilter;
  }

  get isSpaceFilterActive(): boolean {
    return Array.isArray(this.filter.SpaceIds) && this.filter.SpaceIds.length > 0;
  }

  /** Full list of selected Space names — the desktop multiselect truncates it with "…", so this is
   * its hover tooltip. */
  get spaceFilterSummary(): string {
    const labelById = new Map(this.spaceFilterOptions.map((option) => [option.id, option.label]));
    return 'Spaces: ' + this.selectedSpaceIds.map((id) => labelById.get(id) ?? id).join(', ');
  }

  get sortedTagOptions(): Tag[] {
    return [...(this.tags || [])]
      .sort((left, right) => {
        const orderLeft = left.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const orderRight = right.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (orderLeft !== orderRight) {
          return orderLeft - orderRight;
        }
        return left.name.localeCompare(right.name);
      });
  }

  get isDateFilterActive(): boolean {
    if (this.dateFilterMode === 'days') {
      return this.dateSinceDays !== null && this.dateSinceDays > 0;
    }
    if (this.dateFilterMode === 'date') {
      return !!this.normalizeToStartOfDay(this.dateSinceDate);
    }

    const modified = this.filter.ModifiedBetween;
    const created = this.filter.CreatedBetween;
    return !!(modified?.start || modified?.end || created?.start || created?.end);
  }
}
